import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getSshKeyListV3QueryKey,
  getSshKeyListV4QueryKey,
  useSshKeyImportV3,
} from '@newfold/huapi-js';

import useAlerts from '~/components/Alerts/alertsStore';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';

type UseImportKeyOptions = {
  onSuccess?: () => void;
  onError?: () => void;
};

const useImportKey = ({ onSuccess, onError }: UseImportKeyOptions = {}) => {
  const { t } = useTranslation('sshKeyManager');

  const [, { generateAlert }] = useAlerts();

  const queryClient = useQueryClient();

  const setAlertMessage = (variant: string, description: string) => {
    generateAlert({
      severity: variant,
      description,
      showCloseBtn: true,
    });
  };

  const handleError = (errorObj: any) => {
    const error = errorObj?.error;

    const genericError = t('error.addPublicKeyManuallyToast');

    if (!error) {
      setAlertMessage('error', genericError);
      return;
    }

    switch (error) {
      case 'sshImportKeyNameExists':
        setAlertMessage('error', t('error.existingKeyName'));
        break;

      case 'sshImportKeyValueExists':
        setAlertMessage('error', t('error.existingKeyValue'));
        break;

      case 'sshImportPublicKeyMalformed':
        setAlertMessage('error', t('error.publicKeyMalformed'));
        break;

      case 'sshImportPrivateKeyMalformed':
        setAlertMessage('error', t('error.privateKeyInvalid'));
        break;

      case 'sshKeyGenerateError':
        setAlertMessage('error', t('error.publicKeyInvalid'));
        break;

      default:
        setAlertMessage('error', genericError);
        break;
    }
  };

  const { mutate, isPending, isError, isSuccess, reset } = useSshKeyImportV3({
    mutation: {
      onError: (error) => {
        handleError(error?.response?.data);
        onError?.();
      },

      onSuccess: async (_, { hostingId }) => {
        const queryKeyV4 = getSshKeyListV4QueryKey(hostingId);

        const queryKeyV3 = getSshKeyListV3QueryKey(hostingId);

        await invalidateMultipleQueries(queryClient, [queryKeyV4, queryKeyV3]);

        onSuccess?.();
      },
    },
  });

  return {
    mutate,
    isPending,
    isError,
    isSuccess,
    reset,
  };
};

export default useImportKey;
